import { Box, Typography } from "@mui/joy";
import moment from "moment";
import { useState } from "react";
import { CallTheme } from "../entities/callDesign";
import { Message } from "../entities/missionMap/missionMap";
import { useAnswerMutation } from "../redux/missionMap";
import {
  HAIRLINE,
  INK,
  MUTED,
  QUILL_ALIGN_SX,
  SOFT,
} from "../templates/modernInvite";
import { useMissionMap } from "../templates/projectInvite";
import { Response } from "./response";

/**
 * Messages on a modern call — in the page, as content, instead of a chip bar.
 *
 * The old presentation (organisms/messages.tsx, still what legacy pages use)
 * put a horizontal row of buttons ABOVE the masthead, outside the content
 * column. Every chip read "Message" and a timestamp, so five messages were
 * five identical unlabelled buttons; the text itself was two taps away behind
 * a dark full-screen overlay that looked nothing like the page beneath it;
 * messages already read were reachable only through an unlabelled inbox icon;
 * and the bar was tinted `${getContrastYIQ(CompanyColor)}22` — a contrast
 * value used as a fill, which is what made it invisible for dark orgs.
 *
 * Here the message IS the content. Unread ones are open — who sent it, when,
 * and the body — so a musician reads their messages by scrolling rather than by
 * guessing which chip is which. Ones already read fold into a line each behind
 * a disclosure: history should be retrievable without being in the way.
 *
 * Modern calls only; the chip bar is untouched for legacy orgs.
 *
 * Design rules from the email, kept: white ground, no filled color except a
 * response button, the org accent used as ink (the unread rule and its "new"
 * tag) and never as paper.
 */

/** Plain text of a Quill body, for the one-line summary of a read message.
 * DOMParser rather than a tag-stripping regex so entities decode and a stray
 * "<" in the text cannot eat the rest of the line. */
function messagePreview(html?: string): string {
  if (!html) return "";

  const text =
    new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
  const collapsed = text.replace(/\s+/g, " ").trim();

  return collapsed.length > 96 ? `${collapsed.slice(0, 96)}…` : collapsed;
}

/** The kind, the sender and when — the line that tells two messages apart,
 * which is exactly what a row of "Message" chips could not do. */
function MessageMeta({
  message,
  theme,
  relative,
}: {
  message: Message;
  theme: CallTheme;
  relative?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.75,
        alignItems: "center",
        flexWrap: "wrap",
        rowGap: 0.25,
      }}
    >
      {message.Icon ? (
        <Box
          component="img"
          src={message.Icon}
          alt=""
          sx={{ height: 14, width: "auto", display: "block", flexShrink: 0 }}
        />
      ) : null}
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
        {message.Name}
      </Typography>
      {message.CreatedBy ? (
        <Typography sx={{ fontSize: 13, color: SOFT }}>
          from {message.CreatedBy}
        </Typography>
      ) : null}
      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
        {moment(message.CreatedAt).format("MMM D, h:mm A")}
        {relative ? ` · ${moment(message.CreatedAt).fromNow()}` : ""}
      </Typography>
      {!message.Acknowledged ? (
        // Accent as INK, not as a fill: on a modern call the only filled color
        // is a response button.
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: theme.accentText,
          }}
        >
          New
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * @param root0 props
 * @param root0.token the public token whose messages these are
 * @param root0.theme resolved call theme — accent already corrected for text
 * @param root0.hideResponse embedding contexts that answer elsewhere
 * @returns {ReactElement} the inline Messages block, or an empty box
 */
export function CallMessages({
  token,
  theme,
  hideResponse,
}: {
  token: string;
  theme: CallTheme;
  hideResponse?: boolean;
}) {
  const mission = useMissionMap(token);
  const [answer] = useAnswerMutation();
  // Which read messages the musician has opened back up.
  const [opened, setOpened] = useState<number[]>([]);
  const [showEarlier, setShowEarlier] = useState(false);

  // Season tokens carry the messages of every project in the season; mirrors
  // organisms/messages.tsx so both presentations answer identically.
  const season = token[0] === "G";
  const messages = (season ? mission?.seasonMessages() : mission?.Messages) ?? [];

  if (messages.length === 0) return <Box />;

  // A contract is a message in Mercury's model but not in the musician's: it is
  // an ask with its own page and its own signature, surfaced by the contract
  // flow. The chip bar excluded it and so does this, or "Messages" would invite
  // someone to read a contract as news.
  const readable = messages.filter((m) => m.Name !== "Contract");
  if (readable.length === 0) return <Box />;

  // Sorted here rather than trusting the payload's order, and newest first in
  // both blocks.
  //
  // Unread used to run oldest first, on the reasoning that several messages
  // about one call are a conversation and should follow each other. In practice
  // the message that prompted the visit is the newest one, and reading order
  // buried it under everything that came before — the reminder sent a minute ago
  // sat below a documents request from earlier. History was already this way
  // round for the same reason: what somebody goes back for is the recent thing.
  const byNewest = (a: Message, b: Message) =>
    moment(b.CreatedAt).valueOf() - moment(a.CreatedAt).valueOf();

  const unread = readable.filter((m) => !m.Acknowledged).sort(byNewest);
  const earlier = readable.filter((m) => m.Acknowledged).sort(byNewest);

  // A message carrying its own interactors is answered, not dismissed, so it is
  // never part of "mark as read".
  const markable = unread.filter(
    (m) => (m.Data?.Interactors ?? []).length === 0,
  );

  /** Acknowledge one message. On a season the same message is sent per project,
   * so every copy sharing its timestamp is acknowledged together — otherwise it
   * reappears as unread on the next project card. */
  const markAsRead = (message: Message) => {
    if (!season) {
      return answer({
        [mission?.PublicToken ?? ""]: {
          mercuryInteractorID: message.JobStageID,
          jobStageID: message.JobStageID,
        },
      });
    }

    const body: {
      [token: string]: { mercuryInteractorID?: number; jobStageID?: number };
    } = {};
    mission
      ?.allMessages()
      .filter((m) => m.CreatedAt === message.CreatedAt)
      .forEach((m) => {
        // Skipped rather than keyed on `undefined`, which would post a copy
        // under the literal string and acknowledge nothing.
        if (!m.token) return;
        body[m.token] = {
          mercuryInteractorID: m.JobStageID,
          jobStageID: m.JobStageID,
        };
      });

    return answer(body);
  };

  /** Sequentially, not in parallel: on a project every message is acknowledged
   * under the same public token, so the body can only ever carry one jobStageID
   * and concurrent calls would race on the same row. */
  const markAllAsRead = async () => {
    for (const message of markable) {
      await markAsRead(message);
    }
  };

  /** A quiet text action that is still a real control.
   *
   * Visually a link, because with several unread messages a stack of outlined
   * buttons became the loudest thing on the page — backwards, when the message
   * is the point and dismissing it is not. Rendered as a <button> all the same:
   * Joy's Typography is a <p>, and a <p> with an onClick cannot be reached by
   * keyboard or announced as an action. The rules below undo the browser's
   * button chrome so it reads as text.
   *
   * Blue, like every other link on a modern call, never the org color. */
  const textAction = {
    font: "inherit",
    fontSize: 13.5,
    color: "#0b57d0",
    background: "none",
    border: "none",
    p: 0,
    textAlign: "left",
    textDecoration: "underline",
    cursor: "pointer",
    width: "fit-content",
  };

  const body = (message: Message) => (
    <>
      <Box
        sx={{
          fontSize: 14.5,
          lineHeight: 1.55,
          color: "#23272e",
          mt: 0.75,
          "& p": { margin: "0 0 8px 0" },
          "& p:last-child": { marginBottom: 0 },
          ...QUILL_ALIGN_SX,
        }}
        dangerouslySetInnerHTML={{
          __html: message.Data?.Greetings ?? "",
        }}
      />
      {/* A message can carry its own ask — a Documents Request, a revision to
          acknowledge — and those buttons belong with the message that asked. */}
      {message.Data?.Interactors?.length && !hideResponse ? (
        <Box sx={{ mt: 1 }}>
          <Response
            seasonMission={mission}
            selectedMessage={message}
            token={token}
            jobStageID={message.JobStageID}
            overwriteMission={message.Data}
          />
        </Box>
      ) : null}
      {message.Inspectors?.map((inspector) =>
        inspector.Kind === "file" ? (
          <Box key={inspector.ID} sx={{ mt: 1 }}>
            <Typography
              component="a"
              href={inspector.Message}
              target="_blank"
              rel="noreferrer"
              startDecorator={<i className="fa-solid fa-paperclip"></i>}
              sx={{
                // Links are blue on a modern call, never the org color.
                fontSize: 14,
                color: "#0b57d0",
                textDecoration: "underline",
              }}
            >
              {inspector.Title}
            </Typography>
          </Box>
        ) : (
          <Typography
            key={inspector.ID}
            startDecorator={<i className="fa-solid fa-check-double"></i>}
            sx={{ fontSize: 12.5, color: MUTED, mt: 0.75 }}
          >
            {inspector.Message} by {inspector.CreatedBy} on{" "}
            {moment(inspector.CreatedAt).format("lll")}
          </Typography>
        ),
      )}
      {/* Nothing to answer, so reading it is the whole response. */}
      {!message.Acknowledged &&
      (message.Data?.Interactors ?? []).length === 0 ? (
        <Typography
          component="button"
          onClick={() => markAsRead(message)}
          startDecorator={
            <i className="fa-solid fa-check" style={{ fontSize: 11 }}></i>
          }
          sx={{ ...textAction, mt: 1 }}
        >
          Mark as read
        </Typography>
      ) : null}
    </>
  );

  return (
    <Box
      id="call-section-messages"
      sx={{
        width: "100%",
        borderTop: HAIRLINE,
        textAlign: "left",
        scrollMarginTop: { xs: "56px", lg: "16px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: MUTED,
            pt: 2.25,
            pb: 0.75,
          }}
        >
          {unread.length > 0
            ? `Messages · ${unread.length} new`
            : `Messages · ${readable.length}`}
        </Typography>
        {/* One control for the whole stack, once there is a stack. */}
        {markable.length > 1 ? (
          <Typography
            component="button"
            onClick={markAllAsRead}
            sx={textAction}
          >
            Mark all as read
          </Typography>
        ) : null}
      </Box>

      {unread.map((message) => (
        <Box
          key={message.JobStageID}
          sx={{
            // The unread rule: the accent as ink down the edge of what has not
            // been read yet, so the eye finds it without a filled banner.
            borderLeft: `3px solid ${theme.accent}`,
            pl: 1.5,
            py: 1,
            mb: 1.5,
          }}
        >
          <MessageMeta message={message} theme={theme} relative />
          {body(message)}
        </Box>
      ))}

      {earlier.length > 0 ? (
        <Box sx={{ pt: unread.length > 0 ? 0.5 : 0 }}>
          <Typography
            component="button"
            aria-expanded={showEarlier}
            onClick={() => setShowEarlier(!showEarlier)}
            startDecorator={
              <i
                className={`fa-solid fa-chevron-${showEarlier ? "down" : "right"}`}
                style={{ fontSize: 10 }}
              ></i>
            }
            sx={textAction}
          >
            {showEarlier ? "Hide" : "Show"} {earlier.length}{" "}
            {earlier.length === 1 ? "earlier message" : "earlier messages"}
          </Typography>
          {showEarlier
            ? earlier.map((message) => {
                const isOpen = opened.includes(message.JobStageID ?? 0);
                const toggle = () =>
                  setOpened(
                    isOpen
                      ? opened.filter((id) => id !== message.JobStageID)
                      : [...opened, message.JobStageID ?? 0],
                  );

                return (
                  <Box
                    key={message.JobStageID}
                    sx={{ borderTop: "1px solid #f0f2f5", py: 1 }}
                  >
                    {/* The summary is the toggle and the body is its panel,
                        kept siblings on purpose: once open the body carries its
                        own controls, and nesting those inside the toggle would
                        be a control inside a control. role/tabIndex rather than
                        a <button> because the summary holds paragraphs, which a
                        <button> may not. */}
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={toggle}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        toggle();
                      }}
                      sx={{ cursor: "pointer", width: "100%" }}
                    >
                      <MessageMeta message={message} theme={theme} />
                      {!isOpen ? (
                        // The summary a chip could never give: what it said.
                        <Typography
                          sx={{
                            fontSize: 13.5,
                            color: SOFT,
                            mt: 0.25,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {messagePreview(message.Data?.Greetings)}
                        </Typography>
                      ) : null}
                    </Box>
                    {isOpen ? body(message) : null}
                  </Box>
                );
              })
            : null}
        </Box>
      ) : null}
    </Box>
  );
}
